import { useCallback, useEffect, useRef, useState } from "react";

const DEFAULT_PRESS_DURATION_MS = 150;

export interface UsePressTransitionResult<TKey> {
    pressedKey: TKey | null;
    isPressed: (key: TKey) => boolean;
    triggerPress: (key: TKey, action: () => void) => void;
}

/**
 * Defers an action briefly so a CSS press transition can play before the
 * action causes the element to unmount or navigate away.
 *
 * Why: history.push (or any synchronous navigation) fires before a :active
 * style is perceivable. We track which item is "pressed" by key, apply a
 * --pressed class for the duration, then run the action.
 */
export function usePressTransition<TKey = string | number>(
    durationMs: number = DEFAULT_PRESS_DURATION_MS
): UsePressTransitionResult<TKey> {
    const [pressedKey, setPressedKey] = useState<TKey | null>(null);
    const timeoutRef = useRef<number | null>(null);

    useEffect(() => {
        return () => {
            if (timeoutRef.current !== null) {
                window.clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const triggerPress = useCallback((key: TKey, action: () => void) => {
        if (timeoutRef.current !== null) {
            return;
        }
        setPressedKey(key);
        timeoutRef.current = window.setTimeout(() => {
            timeoutRef.current = null;
            setPressedKey(null); // Reset state before or after action
            action();
        }, durationMs);
    }, [durationMs]);

    const isPressed = useCallback((key: TKey) => pressedKey === key, [pressedKey]);

    return { pressedKey, isPressed, triggerPress };
}
